import React, { useState, useEffect } from "react";
import InstituteProgramCard from "../../components/institute/InstituteProgramCard";
import PageHeader from '../../components/PageHeader';

function InstituteAllProgram() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const API_BASE_URL = process.env.REACT_APP_API_LMS;
  const USER_ID = sessionStorage.getItem("id");
  const MOBILE_BREAKPOINT = 768; // Define mobile breakpoint
  const role = sessionStorage.getItem("role");

  const apiHeaders = {
    'Authorization': API_TOKEN,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  // Responsive breakpoints configuration
  const breakpoints = {
    0: {
      slidesPerView: 1,
      spaceBetween: 30
    },
    [MOBILE_BREAKPOINT]: {
      slidesPerView: 2,
      spaceBetween: 30
    },
    1200: {
      slidesPerView: 3,
      spaceBetween: 30
    },
    1600: {
      slidesPerView: 3,
      spaceBetween: 30
    }
  };

  // Get responsive column classes based on breakpoints
  const getResponsiveClasses = () => {
    return "col-12 col-md-6 col-xl-4"; // Modified to show 3 cards on desktop
  };

  // Fetch programs from API
  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-all-programs?role=${role}&category=`, {
        method: 'GET',
        headers: apiHeaders
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data?.programs || [];
    } catch (err) {
      console.error('Error fetching programs:', err);
      throw err;
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/program-category?studentId=${USER_ID}`, {
        method: 'GET',
        headers: apiHeaders
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Error fetching categories:', err);
      throw err;
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch both programs and categories concurrently
        const [programsData, categoriesData] = await Promise.all([
          fetchPrograms(),
          fetchCategories()
        ]);

        setPrograms(programsData);
        setCategories(categoriesData);
      } catch (err) {
        setError(err.message);
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Create filter tabs array
  const filterTabs = [
    "All",
    ...categories.map(category => category.name)
  ];

  // Filter programs based on active filter
  const filteredPrograms = activeFilter === "All" 
    ? programs 
    : programs.filter(program => {
        // Find the category for this program
        const programCategory = categories.find(cat => cat.id === program.category_id);
        return programCategory?.name === activeFilter;
      });

  // Loading state
  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading programs...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 text-center py-5">
            <div className="alert alert-danger" role="alert">
              <h4>Error Loading Programs</h4>
              <p>{error}</p>
              <button 
                className="btn btn-primary" 
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <PageHeader title="All Programs" 
          breadcrumbs={[
            { label: "Dashboard", path: "/institute-dashboard" },
            { label: "All Programs", active: true }
          ]}
        />
      </div>
      
      <div className="row mt-1">
        <div className="col-12">
          <div className="filter-tabs d-flex flex-wrap gap-2 gap-md-3 justify-content-left">
            {filterTabs.map((tab) => (
              <button
                key={tab}
                className={`
                  btn px-2 px-md-4 py-1 py-md-2 rounded-pill 
                  transition-all duration-200
                  fs-7 fs-md-6 fw-semibold
                  hover:shadow-lg
                  ${activeFilter === tab
                    ? "btn-warning text-dark fw-bold shadow-sm"
                    : "btn-soft-primary text-primary hover:btn-primary hover:text-white"
                  }
                `}
                onClick={() => setActiveFilter(tab)}
              >
                <span className="d-inline-block">{tab}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Programs Grid with Responsive Breakpoints */}
      <div className="row g-3" style={{ '--bs-gutter-x': '30px' }}>
        {filteredPrograms.map((program) => (
          <div key={program.id} className={getResponsiveClasses()}>
            <InstituteProgramCard programData={program} />
          </div>
        ))}
      </div>

      {/* No Results Message */}
      {filteredPrograms.length === 0 && !loading && (
        <div className="row">
          <div className="col-12 text-center py-5">
            <h4 className="text-muted">No programs found for "{activeFilter}"</h4>
            <p className="text-muted">Try selecting a different filter or check back later.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstituteAllProgram;